import { IS_BROWSER } from '$fresh/runtime.ts'
import Searchbar from '$store/components/header/Searchbar.tsx'
import NewSearchbar from '$store/components/header/NewSearchbar.tsx'
import type { Props as SearchbarProps } from '$store/components/search/Searchbar.tsx'
import Icon from '$store/components/ui/Icon.tsx'
import PureButton from '$store/components/ui/PureButton.tsx'
import clsx from '$store/sdk/clsx.ts'
import { useSpeechRecognition } from '$store/sdk/speechRecognition.tsx'
import { useUI } from '$store/sdk/useUI.ts'
import { effect } from '@preact/signals'

type Props = {
	searchbar: SearchbarProps
	isHomePage: boolean
	/**
	 * @ignore
	 */
	isMobile: boolean

	searchOpen?: boolean
}

export default function SearchButton({
	searchbar,
	isHomePage,
	isMobile,
	searchOpen = true,
}: Props) {
	const { displaySearchPopup, displaySearchPopupMobile } = useUI()

	const speech = useSpeechRecognition({
		onSpeech: () => {
			const m = document.querySelectorAll(
				'.microphone',
			) as NodeListOf<HTMLInputElement>

			m.forEach((i) => {
				i.value = speech.detectedText.value
			})
		},
		onError: (e) => console.error(e),
	})

	effect(() => {
		if (!IS_BROWSER) return

		document.querySelectorAll('.microphone-icon').forEach((i) => {
			if (speech.isListening.value) {
				i.classList.add('text-red-500')
			} else {
				i.classList.remove('text-red-500')
			}
		})
	})

	return (
		<>
			{isMobile
				? (
					<>
						{/* Mobile Version */}
						{searchOpen
							? (
								<div class={'relative w-full flex justify-center pb-2'}>
									<div
										class={'flex justify-center gap-1 border-b border-[#FFFFFF80] h-10'}
									>
										<PureButton
											type='button'
											class={clsx(
												'place-items-center relative w-10 h-10 flex items-center justify-center',
												!speech.supported &&
													'pointer-events-none opacity-0',
											)}
											tabIndex={-1}
											onClick={speech.start}
										>
											<Icon
												id='Microphone'
												size={24}
												strokeWidth={0.01}
												class='microphone-icon text-white shrink-0'
											/>
											<span class='sr-only'>Procurar por voz</span>
										</PureButton>

										<button
											class=''
											onClick={() => (displaySearchPopup.value = true)}
										>
											<p
												class={'text-xs text-white w-48 flex items-center justify-center gap-2 group z-[100] h-full'}
											>
												O que você procura hoje?
											</p>
										</button>

										<button
											type='text'
											class='w-10 shrink-0 flex justify-center items-center'
											onClick={(e) => {
												const text = (
													e.currentTarget
														.previousElementSibling as HTMLInputElement
												).value

												if (text) {
													location.href = `/s?q=${text}`
												}
											}}
										>
											<Icon
												id='MagnifyingGlass'
												class='flex-shrink-0 text-white'
												width={24}
												htmlFor='searchbar'
												height={24}
											/>
										</button>
									</div>
									<Searchbar searchbar={searchbar} />
								</div>
							)
							: displaySearchPopupMobile.value
							? (
								<div class={'relative w-full flex justify-center pb-3 px-4'}>
									<div
										class={'flex justify-between gap-1 border border-[#FFFFFF80] h-10 rounded w-full '}
									>
										<PureButton
											type='button'
											class={clsx(
												'place-items-center relative w-10 h-9 flex items-center justify-center',
												!speech.supported &&
													'pointer-events-none opacity-0',
											)}
											tabIndex={-1}
											onClick={speech.start}
										>
											<Icon
												id='Microphone'
												size={24}
												strokeWidth={0.01}
												class='microphone-icon text-white shrink-0'
											/>
											<span class='sr-only'>Procurar por voz</span>
										</PureButton>

										<button
											class='w-full'
											onClick={() => (displaySearchPopup.value = true)}
										>
											<p
												class={'text-xs px-2 text-white w-full flex items-center justify-start gap-2 group z-[100] h-full'}
											>
												O que você procura hoje?
											</p>
										</button>

										<button
											type='text'
											class='w-10 shrink-0 flex justify-center items-center'
											onClick={(e) => {
												const text = (
													e.currentTarget
														.previousElementSibling as HTMLInputElement
												).value

												if (text) {
													location.href = `/s?q=${text}`
												}
											}}
										>
											<Icon
												id='MagnifyingGlass'
												class='flex-shrink-0 text-white'
												width={24}
												htmlFor='searchbar'
												height={24}
											/>
										</button>
									</div>
									<NewSearchbar searchbar={searchbar} searchOpen={searchOpen} />
								</div>
							)
							: <></>}
					</>
				)
				: (
					<>
						{/* Desktop Version */}
						{displaySearchPopup.value && (
							<div
								class='w-screen h-screen bg-black/50 absolute inset-0 z-[99]'
								onClick={() => {
									displaySearchPopup.value = false
									if (isHomePage) {
										document
											.querySelector('.root')!
											.classList.remove('header-hover')
									}
								}}
							/>
						)}

						<button
							class='flex items-center gap-2 group relative z-[100]'
							onClick={() => (displaySearchPopup.value = true)}
						>
							<Icon
								id='MagnifyingGlass'
								class='flex-shrink-0 text-white '
								width={24}
								htmlFor='searchbar'
								height={24}
							/>

							<div class='text-sm whitespace-nowrap'>
								<p class='text-white group-data-[micro-header="true"]/header:text-white'>
									BUSCAR
								</p>
							</div>

							<Searchbar searchbar={searchbar} />
						</button>
					</>
				)}
		</>
	)
}